import {createSlice} from '@reduxjs/toolkit';
import API from '../../common/api';
import {setSpinner} from '../spinner/spinnerSlice';
import {setError, setLoginError} from '../error/errorSlice';
import {setNote} from '../note/noteSlice';
import {setAmplitudeUserProperties} from '../../analytics/amplitude';
import Cookies from 'js-cookie';

const { REACT_APP_STRIPE_PK } = process.env;
const { REACT_APP_STRIPE_NAME } = process.env;

export const authSlice = createSlice({
    name: 'auth',
    initialState: {
        email: null,
        password: null,
        token: null,
        status: null,
        userID: null,
        userName: '',
        userUuid: null,
        stripePublishKey: REACT_APP_STRIPE_PK,
        stripeAccountName: REACT_APP_STRIPE_NAME,
        stripePublishKey2: REACT_APP_STRIPE_PK,
        stripeAccountName2: REACT_APP_STRIPE_NAME,
        btToken: null,
        registrationSource: 'email', //default case is 'email' as per pseudo flow
        deeplinkUrl: '',
        deeplinkQR: '',
    },
    reducers: {
        setResult: (state, action) => {
            const {access_token, entity: {geozilla_user_id, name, email, registration_source}} = action.payload;
            state.token = access_token;
            state.status = true;
            state.userID = geozilla_user_id;
            state.userName = name;
            state.email = email;
            state.registrationSource = registration_source;
        },
        resetAuth: (state, action) => {
            state.status = null;
            state.userID = null;
            state.userName = '';
            state.email = null;
            state.token = null;
        },
        setToken: (state, action) => {
            state.token = action.payload;
        },
        setUserUuid: (state,action) => {
            state.userUuid = action.payload;
        },
        setBtToken: (state, action) => {
            state.btToken = action.payload;
        },
        setStripeData: (state, action) => {
            const { stripePublishKey, stripeAccountName } = action.payload;

            state.stripePublishKey = stripePublishKey;
            state.stripeAccountName = stripeAccountName;
        },
        setAlternativeStripeData: (state, action) => {
            state.stripePublishKey2 = action.payload.stripePublishKey2;
            state.stripeAccountName2 = action.payload.stripeAccountName2;
        },
        setAlternativeStripeDataAsMain: (state, action) => {
            state.stripePublishKey = state.stripePublishKey2;
            state.stripeAccountName = state.stripeAccountName2;
        },
        setDeeplinkData: (state, action) => {
            const { short_deeplink, qr_code } = action.payload;
            state.deeplinkUrl = short_deeplink;
            state.deeplinkQR = qr_code;
        },
    },
});

export const {
    setResult,
    resetAuth,
    setToken,
    setUserUuid,
    setStripeData,
    setAlternativeStripeData,
    setAlternativeStripeDataAsMain,
    setBtToken,
    setDeeplinkData,
} = authSlice.actions;


export const sendLoginRequest = params => async dispatch => {
    dispatch(setSpinner(true));
    try {
        const res = await API.loginEmail(params);
        dispatch(setResult(res.data));
        Cookies.set('userUuid', res.data.entity.uuid, { expires: 365 });
        dispatch(setLoginError(false));
        dispatch(getDeeplinkData(res.data.access_token));
    } catch (e) {
        dispatch(setLoginError(true));
    } finally {
        dispatch(setSpinner(false));
    }
};

export const sendSocialLoginRequest = params => async dispatch => {
    dispatch(setSpinner(true));
    try {
        let res;
        if (params.driver === 'google') {
            res = await API.loginViaGoogle(params);
        } else {
            res = await API.loginSoc(params);
        }
        dispatch(setResult(res.data));
        Cookies.set('userUuid', res.data.entity.uuid, { expires: 365 });
        dispatch(getDeeplinkData(res.data.access_token));
    } catch (e) {
        dispatch(setError({show: true, type: `login${params.driver === 'google' ? 'Google' : 'Social'}`, text: e.response.data.errors.message}));
    } finally {
        dispatch(setSpinner(false));
    }
};

export const sendPassResetRequest = params => async dispatch => {
    dispatch(setSpinner(true));

    API.resetPassword(params).then(
        data => dispatch(setNote({
            show: true,
            text: 'We\'ve sent the link to reset your password. Please check your email.'
        })),
        error => dispatch(setError({show: true, type: 'resetPass', text: 'Failed to reset password'}))
    ).finally(() => dispatch(setSpinner(false)));
};

export const getDeeplinkData = token => dispatch => {
    API.getDeeplinkData(token)
        .then((response) => {
            const data = response.data || response;
            if (data && data.short_deeplink && data.qr_code) {
                dispatch(setDeeplinkData(data));
            }
        })
        .catch((error) => console.log(error));
};

export const getPaymentSettings = token => dispatch => {
    API.getPaymentSettings(token)
        .then((data) => {
            let {
                stripe_account_name,
                stripe_publishable_key,
                alternative_stripe_account,
            } = data;
            let userProperties = {
                'Payments gateways': `Stripe [${stripe_account_name}]`,
                '3Ds_test': data.experiments?.force_3ds_for_eu_customers || null,
            };
            setAmplitudeUserProperties(userProperties);
            dispatch(
                setStripeData({
                    stripePublishKey: stripe_publishable_key,
                    stripeAccountName: stripe_account_name,
                })
            );
            if (alternative_stripe_account) {
                dispatch(setAlternativeStripeData(alternative_stripe_account));
            }
        })
        .catch((error) => console.log(error));
};

export const selectEmail = state => state.auth.email;
export const selectToken = state => state.auth.token;
export const selectUsername = state => state.auth.userName;
export const selectUserID = state => state.auth.userID;
export const selectUserUuid = state => state.auth.userUuid;
export const selectBtToken = state => state.auth.btToken;
export const selectRegistrationSource = state => state.auth.registrationSource;
export const selectStripePublishKey = state => state.auth.stripePublishKey;
export const selectStripeAccountName = state => state.auth.stripeAccountName;
export const selectDeeplinkUrl = state => state.auth.deeplinkUrl;
export const selectDeeplinkQR = state => state.auth.deeplinkQR;


export default authSlice.reducer;